const Lead = window.VestfirmaLeadCustomer || {};
const phoneDigits = Lead.phoneDigits || ((value) => String(value || '').replace(/\D/g, ''));
const formatBrazilPhone = Lead.formatBrazilPhone || ((digits) => digits);
const bindPhoneInput = Lead.bindPhoneInput || (() => {});
const validatePhoneInput = Lead.validatePhoneInput || (() => true);

class VestfirmaLeadForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    const phoneInvalid =
      this.form.dataset.phoneInvalid || 'Informe um WhatsApp válido com DDD (10 ou 11 dígitos).';

    this.form.querySelectorAll('[data-lead-phone]').forEach((input) => {
      bindPhoneInput(input, phoneInvalid);
    });

    if (this.form.dataset.leadMode !== 'steps') return;

    this.steps = Array.from(this.querySelectorAll('[data-lead-step]'));
    this.progress = this.querySelector('[data-lead-progress]');
    this.progressBar = this.querySelector('[data-lead-progress-bar]');
    this.backBtn = this.querySelector('[data-lead-back]');
    this.nextBtn = this.querySelector('[data-lead-next]');
    this.submitBtn = this.querySelector('[data-lead-submit]');
    this.bodyField = this.form.querySelector('[data-lead-body]');
    this.phoneInvalid = phoneInvalid;
    this.current = 0;

    this.backBtn?.addEventListener('click', () => this.go(-1));
    this.nextBtn?.addEventListener('click', () => this.go(1));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.form.addEventListener('keydown', (event) => this.onKeydown(event));

    this.renderStep();
  }

  onKeydown(event) {
    if (event.key !== 'Enter') return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
    if (this.current >= this.steps.length - 1) return;

    event.preventDefault();
    this.go(1);
  }

  go(delta) {
    if (delta > 0 && !this.validateStep(this.current)) return;
    this.current = Math.max(0, Math.min(this.steps.length - 1, this.current + delta));
    this.renderStep({ focus: true });
  }

  validateStep(index) {
    const step = this.steps[index];
    if (!step) return true;

    const fields = step.querySelectorAll('input, select, textarea');
    for (const field of fields) {
      if (field.disabled || field.type === 'hidden') continue;

      if (field.matches('[data-lead-phone]')) {
        if (!validatePhoneInput(field, this.phoneInvalid)) {
          try {
            field.focus({ preventScroll: true });
          } catch {
            field.focus();
          }
          return false;
        }
        continue;
      }

      if (!field.checkValidity()) {
        field.reportValidity();
        try {
          field.focus({ preventScroll: true });
        } catch {
          field.focus();
        }
        return false;
      }
    }
    return true;
  }

  renderStep(options = {}) {
    const shouldFocus = options.focus === true;
    this.steps.forEach((step, index) => {
      step.hidden = index !== this.current;
    });

    const total = this.steps.length;
    const label = this.progress?.dataset.stepLabel || 'Etapa';
    if (this.progress) {
      this.progress.textContent = `${label} ${this.current + 1} de ${total}`;
    }

    if (this.progressBar && total > 0) {
      const pct = ((this.current + 1) / total) * 100;
      this.progressBar.style.width = `${pct}%`;
    }

    if (this.backBtn) this.backBtn.hidden = this.current === 0;
    if (this.nextBtn) this.nextBtn.hidden = this.current === total - 1;
    if (this.submitBtn) this.submitBtn.hidden = this.current !== total - 1;

    if (!shouldFocus) return;

    const activeStep = this.steps[this.current];
    const focusable = activeStep?.querySelector(
      'input:not([type="hidden"]):not([aria-hidden="true"]), select, textarea'
    );
    if (focusable instanceof HTMLElement) {
      requestAnimationFrame(() => {
        try {
          focusable.focus({ preventScroll: true });
        } catch {
          focusable.focus();
        }
      });
    }
  }

  onSubmit(event) {
    if (!this.validateStep(this.current)) {
      event.preventDefault();
      return;
    }

    const phoneField = this.form.querySelector('[name="contact[phone]"]');
    if (phoneField) {
      phoneField.value = phoneDigits(phoneField.value);
    }

    const summary = this.buildSummaryMessage();
    if (summary) {
      try {
        sessionStorage.setItem('vestfirma-lead-thanks', summary);
      } catch {
        /* a página de obrigado abre o WhatsApp com a mensagem padrão */
      }
    }

    if (this.bodyField) {
      const defaultBody = this.bodyField.dataset.defaultBody || '';
      this.bodyField.value = summary || defaultBody;
    }

    if (this.dataset.customerTag || this.dataset.customerApi) {
      this.syncLeadCustomer().catch(() => {});
    }
  }

  buildSummaryMessage() {
    const parts = [];
    const push = (label, name) => {
      const el = this.form.querySelector(`[name="${name}"]`);
      if (el && el.value && String(el.value).trim()) {
        parts.push(`${label}: ${String(el.value).trim()}`);
      }
    };

    push('WhatsApp', 'contact[phone]');
    push('Nome', 'contact[name]');
    push('E-mail', 'contact[email]');
    push('Empresa', 'contact[empresa]');
    push('Quantidade', 'contact[quantidade]');
    push('Prioridade', 'contact[prioridade]');
    push('Segmento', 'contact[segmento]');
    push('Finalidade', 'contact[finalidade]');
    push('Origem', 'contact[origem]');

    const freeMessage = this.form.querySelector('[name="contact[body]"]:not([data-lead-body])');
    if (freeMessage?.value?.trim()) {
      parts.push(`Mensagem: ${freeMessage.value.trim()}`);
    }

    const intro = (this.dataset.waIntro || '').trim();
    const payNote = (this.dataset.payNote || '').trim();
    const blocks = [];
    if (intro) blocks.push(intro);
    if (parts.length) blocks.push(parts.join('\n'));
    if (payNote) blocks.push(payNote);
    return blocks.join('\n\n');
  }

  buildCustomerPayload() {
    const read = (name) => {
      const el = this.form.querySelector(`[name="${name}"]`);
      return el && el.value ? String(el.value).trim() : '';
    };

    return {
      tag: this.dataset.customerTag || 'orçamento',
      email: read('contact[email]'),
      name: read('contact[name]'),
      phone: read('contact[phone]'),
      company: read('contact[empresa]'),
      quantity: read('contact[quantidade]'),
      priority: read('contact[prioridade]'),
      segment: read('contact[segmento]'),
      purpose: read('contact[finalidade]'),
      source: read('contact[origem]'),
      note: this.bodyField?.value || '',
    };
  }

  resolveCustomerApiUrl() {
    if (Lead.resolveCustomerApiUrl) {
      return Lead.resolveCustomerApiUrl(this.dataset.customerApi);
    }
    const configured = (this.dataset.customerApi || '').trim();
    return configured || '/apps/vestfirma-leads/customer';
  }

  async syncLeadCustomer() {
    const payload = this.buildCustomerPayload();
    if (!payload.email) return;

    const api = this.resolveCustomerApiUrl();
    if (Lead.syncLeadCustomer) {
      await Lead.syncLeadCustomer(api, payload);
      return;
    }

    const response = await fetch(api, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
      keepalive: true,
    });

    if (!response.ok) {
      let detail = '';
      try {
        const data = await response.json();
        detail = data.error || JSON.stringify(data);
      } catch {
        detail = await response.text();
      }
      console.warn('[vestfirma-lead] Cliente não cadastrado:', response.status, detail);
    }
  }
}

if (!customElements.get('vestfirma-lead-form')) {
  customElements.define('vestfirma-lead-form', VestfirmaLeadForm);
}

function initLeadHeroVideos() {
  document.querySelectorAll('[data-lead-hero-video]').forEach((media) => {
    const video = media.querySelector('.vestfirma-lead__visual-video');
    const muteBtn = media.querySelector('[data-lead-video-mute]');
    if (!(video instanceof HTMLVideoElement)) return;

    const applyMuteState = () => {
      const unmuted = muteBtn?.classList.contains('is-unmuted');
      video.muted = !unmuted;
      if (unmuted) {
        video.removeAttribute('muted');
      } else {
        video.setAttribute('muted', '');
      }
    };

    const playVideo = () => {
      applyMuteState();
      video.play().catch(() => {});
    };

    muteBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      const unmuted = !muteBtn.classList.contains('is-unmuted');
      muteBtn.classList.toggle('is-unmuted', unmuted);
      muteBtn.setAttribute('aria-pressed', unmuted ? 'true' : 'false');
      muteBtn.setAttribute(
        'aria-label',
        unmuted ? muteBtn.dataset.labelMute || 'Silenciar vídeo' : muteBtn.dataset.labelUnmute || 'Ativar som'
      );
      applyMuteState();
      if (unmuted) playVideo();
    });

    video.addEventListener('loadeddata', playVideo, { once: true });
    playVideo();

    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            playVideo();
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(video);
  });
}

function initLeadPage() {
  initLeadHeroVideos();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLeadPage);
} else {
  initLeadPage();
}
